import { RedeemDisplayInfo, Reward, OrderWithContext, PurchasedReward, CustomerProfile, OrderErrorCode, OrderApiException } from '@/models';
import { Routes } from '@/router';
import { AnalyticsEventCategory, AnalyticsEventAction, AnalyticsEventLabel } from '@/services/google/google-analytics.services';
import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { ButtonType } from '@/components/CmgButton/cmg-button';
import Lottie, { AnimationConfig } from 'lottie-web';
import { loadScript, isDesktop } from '@/helpers';
import { AccountManagementEvent } from '@/services/analytics/events';
import { InteractionTrackingType } from '@/services/analytics/platforms/adobe-analytics/models';


@Component({
  name: 'CmgRewardRedemptionSuccessModal',
})
  
export default class CmgRewardRedemptionSuccessModal extends Vue {

  isLottieLibraryLoaded = false;

  get promotion() {
    return this.$appStore.state.ui.modals.rewardRedemptionSuccess.args?.promotion;
  }

  get purchasedReward() {
    return this.$appStore.state.ui.modals.rewardRedemptionSuccess.args?.purchasedReward;
  }

  get rewardExpirationDate(): string {
    return this.promotion?.expirationDate || '';
  }

  get container() {
    return document.getElementById('achievementLottie')!;
  }

  animationComplete = () => this.container.remove();
  
  get qrWidth(): number {
    return document.documentElement.clientWidth * 0.5;
  }

  async mounted() {
    await this.loadLottieJsLibrary();
  }

  get customer(): CustomerProfile | null {
    const profile = this.$appStore.state.customer.profile;
    return profile && !profile.isGuest ? profile : null;
  }

  get showErrorMessage(): boolean {
    return !!this.$appStore.state.ui.modals.rewardRedemptionSuccess.args!.showErrorMessage;
  }

  get goodsTitle(): string {
    if (this.customGoodsReward) {
      return this.customGoodsReward.title;
    }
    return 'TIME TO SHOP CHIPOTLE GOODS.';
  }

  get customGoodsReward(): RedeemDisplayInfo | undefined {
    return this.$appStore.state.ui.modals.rewardRedemptionSuccess.args!.redeemDisplayInfo;
  }

  get customGoodsDescription() {
    return this.customGoodsReward?.description;  
  }

  setLoadingStatus(loading: boolean) {
    this.$services.ui.setAppBlur(loading);
    this.$services.ui.setAppPepperVisibility(loading);
  }

  get isMobile():boolean {
    return this.$services.ui.isMobile();
  }

  get currentOrder(): OrderWithContext | null {
    return this.$store.getters.currentOrder;
  }
 
  get lottieIntroSparkUrl(): string {
    return this.$appStore.state.cms.animations.bag.lottieIntroSparkUrl;
  }

  get lottieBackgroundSparklesUrl(): string {
    return this.$appStore.state.cms.animations.bag.lottieBackgroundSparklesUrl;
  }

  async loadLottieJsLibrary(): Promise<any> {
    if (!this.isLottieLibraryLoaded) {
      this.isLottieLibraryLoaded = true;
      return await Promise.all([
        loadScript('https://unpkg.com/@lottiefiles/lottie-interactivity@latest/dist/lottie-interactivity.min.js'),
        loadScript('https://unpkg.com/@lottiefiles/lottie-player@1.5.7/dist/lottie-player.js')
      ]);
    }
  }

  get happyAvocadoUrl(): string {
    return this.$appStore.state.cms.images.feedback.happyAvocado.default;
  }

  get sadAvocadoUrl(): string {
    return this.$appStore.state.cms.images.feedback.sadAvocado.default;
  }

  get visible(): boolean {
    return this.$appStore.state.ui.modals.rewardRedemptionSuccess.visible;
  }

  get reward(): Reward {
    return this.$appStore.state.ui.modals.rewardRedemptionSuccess.args!.reward;
  }

  get rewardTitle(): string {
    return this.reward.title
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  get offerType(): string {
    return this.reward.offerType.toLowerCase();
  }

  get rewardPromoCode(): string {
    let promocode = this.$appStore.state.ui.modals.rewardRedemptionSuccess.args!.promotionCode!;
    this.$services.order.setPromoCode(promocode);
    return promocode;
  }

  onClickRewardsExchange() {
    this.$router.push({ name: Routes.RewardsExchange });
    this.onClose();
  }

  onClickDone() {
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardDetailModal,
      action: AnalyticsEventAction.DoneSuccessMessageClicked,
      label: AnalyticsEventLabel.DoneSuccessMessageClicked,
    });
    this.onClose();
  }

  private handleAddToBagException(e: any) {
    if (!(e instanceof OrderApiException)) {
      throw e;
    }

    const error = e.errors[0];
    if (!error) {
      throw e;
    }

    switch (error.errorCode) {
      case OrderErrorCode.RewardExceedsLimit:
        this.$services.ui.setModalVisibility('toast', true, {
          title: '',
          body: 'Only one reward per order.',
          bodyOnly: true,
        });
        Vue.nextTick(() => {
          setTimeout(() => {
            this.$services.ui.nextFocus('reward-heading');
          }, 6000);
        });
        break;
    }
  }

  async onClickRedeem() {
    const reward = this.reward;
    const order = this.currentOrder;

    if (!order || !reward) {
      return;
    }

    this.$services.ui.setAppPepperVisibility(true);
    await this.$services.reward
      .purchaseAndApplyReward(reward.id, order)
      .then(async () => {
        await this.$services.order.refreshOrder(order, true);
        this.$services.ui.setRewardConfirmationVisibility(true);
      })
      .catch(() => {
        const onClose = () => {
          this.$services.ui.setModalVisibility('basicError', false);
        };

        this.$services.ui.setModalVisibility('basicError', true, {
          header: `OH BUMMER.`,
          content: 'There was an error redeeming your entree, please try again later',
          background: 'white',
          options: [
            {
              text: 'Okay',
              type: ButtonType.Primary,
              onClick: onClose,
            },
          ],
          onClose,
        });
      })
      .finally(() => {
        this.$services.loyalty.refreshPoints(true);
        this.$services.ui.setAppPepperVisibility(false);
        this.onClose();
      });
  }

  async onClickAddToBag() {
    this.$services.ui.setLastSelectedElement('bag');
    this.$services.analytics.send(new AccountManagementEvent(InteractionTrackingType.RewardsAddedToBag, undefined, this.reward.title, 'Reward Redemption Success'));

    let order = this.$services.order.getCurrentOrder();
    const promotionCode = this.rewardPromoCode;
    this.setLoadingStatus(true);
    if (!order) {
      if (this.$appStore.state.restaurant.selectedRestaurant) {
        order = await this.$services.order.createOrder(this.$appStore.state.restaurant.selectedRestaurant!.restaurantNumber);
        this.setLoadingStatus(false);
      } else {
        this.$services.ui.setModalVisibility('rewardRedemptionSuccess', false, this.savedState)
        this.$services.ui.setModalVisibility('findAChipotle', true, {
          onClose: async () => {
            if (!order || !this.$appStore.state.restaurant.selectedRestaurant) {
              return;
            }

            try {
              order = await this.$services.order.createOrder(this.$appStore.state.restaurant.selectedRestaurant.restaurantNumber, );

              if (order && promotionCode) {
                await this.$services.order.applyDiscountToOrder(order, promotionCode);

                this.$services.bag.setBagVisibility(true);
              }
            } catch (e) {
              this.handleAddToBagException(e);
            }
          },
        });

        this.$services.ui.setModalVisibility('toast', true, {
          body: 'Before you order, we gotta find your Chipotle first.'.toUpperCase(),
          bodyOnly: true,
        });
        Vue.nextTick(() => this.$services.ui.focusFirstDiv('.cmg-modal-container'));
        return;
      }
    }

    if (!order) {
      return;
    }
    try {
      if (!order.discounts || !order.discounts.find(d => d.promotionCode === promotionCode)) {
          
        await this.$services.order.applyDiscountToOrder(order, promotionCode);
        this.$services.bag.setBagVisibility(true);
          this.$nextTick(() => {
            Vue.nextTick(() => this.$services.ui.focusDiv('.fullModal'));
            this.$services.ui.setLastSelectedElement('bag');
        });
      } else {
        await this.$services.order.removeDiscountFromOrder(order, this.rewardPromoCode);
        this.$services.bag.setBagVisibility(true);
          Vue.nextTick(() => this.$services.ui.focusDiv('.fullModal'));
      }
      
    } catch (e) {
      this.handleAddToBagException(e);
    } finally {
      this.setLoadingStatus(false);
      this.onClose();
    }
  }

  onClickViewRewards() {
    if (this.showErrorMessage) {
      this.$services.googleAnalytics.sendCustomEvent({
        category: AnalyticsEventCategory.RewardDetailModal,
        action: AnalyticsEventAction.RedemptionPurchaseError,
        label: AnalyticsEventLabel.RedemptionPurchaseError,
      });
    } else {
      this.$services.googleAnalytics.sendCustomEvent({
        category: AnalyticsEventCategory.RewardDetailModal,
        action: AnalyticsEventAction.ViewInMyRewardsClick,
        label: AnalyticsEventLabel.ViewInMyRewardsClick,
      });
    }

    this.$router.push({ name: Routes.Rewards, hash: '#rewards' });
    this.onClose();
  }
  
  get savedState() {
    return {
      redeemDisplayInfo: this.customGoodsReward,
      promotion: this.promotion,
      promotionCode: this.rewardPromoCode,
      reward:this.reward,
      purchasedReward: this.purchasedReward
    }
  }

  onClickTermsAndConditions(event: any) {
    const reward = this.reward;
    const savedState = Object.assign({}, this.savedState);
    const onBack = () => {
      this.$services.ui.setModalVisibility('rewardRedemptionSuccess', true, savedState);
      this.$services.ui.setModalVisibility('rewardTermsAndConditions', false);
    };

    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    const terms = this.customGoodsReward ? this.customGoodsReward.terms : reward.terms;
    this.$services.ui.setModalVisibility('rewardTermsAndConditions', true, { termsAndConditions: terms, onBack });
    this.$services.ui.firstFocus();
    this.$services.ui.setModalVisibility('rewardRedemptionSuccess', false, savedState);
  }

  onClose() {
    this.$services.ui.setModalVisibility('rewardRedemptionSuccess', false);
    this.$services.ui.setModalVisibility('account', false);
  }
}
